/** Real-time order lock/unlock via WebSocket. Calls onOrderLockEvent when server pushes events. */
import { useCallback, useEffect, useRef, useState } from 'react';

const PING_INTERVAL_MS = 30_000;
const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 20;
const NORMAL_CLOSE_CODE = 1000;

export interface OrderLockEvent {
  eventType: 'order_locked' | 'order_unlocked';
  orderId: string;
  jobLocation: string;
  lockedBy: string;
  leaseExpiresAt?: string;
  timestamp: string;
}

export interface UseOrderWebSocketOptions {
  userId?: string;
  jobLocations?: string[];
  onOrderLockEvent?: (event: OrderLockEvent) => void;
  enabled?: boolean;
}

export interface UseOrderWebSocketReturn {
  isConnected: boolean;
  reconnectAttempts: number;
}

interface WsMessageBase {
  type: string;
  data?: unknown;
}

function getWsBaseUrl(): string {
  const env = import.meta.env.VITE_WS_URL;
  if (env?.trim()) return env.trim();
  const fallback = 'wss://xg615ecipc.execute-api.ca-central-1.amazonaws.com/dev/';
  return fallback;
}

function buildWsUrl(userId?: string, jobLocations?: string[]): string {
  const base = getWsBaseUrl();
  const params = new URLSearchParams();
  if (userId) params.set('userId', userId);
  if (jobLocations?.length) params.set('jobLocations', jobLocations.join(','));
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

function isOrderLockMessage(msg: unknown): msg is WsMessageBase & { type: 'order_locked' | 'order_unlocked'; data: OrderLockEvent } {
  if (typeof msg !== 'object' || msg === null || !('type' in msg)) return false;
  const t = (msg as WsMessageBase).type;
  return t === 'order_locked' || t === 'order_unlocked';
}

export function useOrderWebSocket({
  userId,
  jobLocations,
  onOrderLockEvent,
  enabled = true,
}: UseOrderWebSocketOptions): UseOrderWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const enabledRef = useRef(enabled);
  const onOrderLockEventRef = useRef(onOrderLockEvent);
  const jobLocationsRef = useRef(jobLocations);

  useEffect(() => {
    onOrderLockEventRef.current = onOrderLockEvent;
  }, [onOrderLockEvent]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    jobLocationsRef.current = jobLocations;
  }, [jobLocations]);

  const clearPingInterval = useCallback(() => {
    if (pingIntervalRef.current !== null) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const sendSubscribe = useCallback((ws: WebSocket, locations: string[]) => {
    if (ws.readyState === WebSocket.OPEN && locations.length > 0) {
      ws.send(JSON.stringify({ type: 'subscribe', jobLocations: locations }));
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!enabledRef.current) return;
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) return;

    clearReconnectTimer();
    const attempt = reconnectAttemptsRef.current;
    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * 2 ** attempt,
      RECONNECT_MAX_DELAY_MS
    );
    reconnectAttemptsRef.current += 1;
    setReconnectAttempts(reconnectAttemptsRef.current);
    reconnectTimerRef.current = setTimeout(() => {
      connectRef.current();
    }, delay);
  }, [clearReconnectTimer]);

  const connect = useCallback(() => {
    if (!enabledRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (wsRef.current?.readyState === WebSocket.CONNECTING) return;

    const url = buildWsUrl(userId, jobLocationsRef.current ?? undefined);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        setReconnectAttempts(0);

        const locations = jobLocationsRef.current;
        if (locations?.length) {
          sendSubscribe(ws, locations);
        }

        clearPingInterval();
        pingIntervalRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const msg = JSON.parse(event.data as string) as unknown;
          if (isOrderLockMessage(msg)) {
            const payload = msg.data as Partial<OrderLockEvent> | undefined;
            const lockEvent: OrderLockEvent =
              payload?.eventType != null
                ? (payload as OrderLockEvent)
                : { ...payload, eventType: msg.type as OrderLockEvent['eventType'] } as OrderLockEvent;
            onOrderLockEventRef.current?.(lockEvent);
          }
        } catch {
          // ignore non-JSON or non lock/unlock messages
        }
      };

      ws.onclose = (event: CloseEvent) => {
        setIsConnected(false);
        clearPingInterval();
        wsRef.current = null;
        if (enabledRef.current && event.code !== NORMAL_CLOSE_CODE) {
          scheduleReconnect();
        }
      };

      ws.onerror = () => {
        // Reconnect is handled in onclose
      };
    } catch {
      scheduleReconnect();
    }
  }, [userId, clearPingInterval, sendSubscribe, scheduleReconnect]);

  const connectRef = useRef(connect);
  connectRef.current = connect;

  useEffect(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && jobLocations?.length) {
      sendSubscribe(wsRef.current, jobLocations);
    }
  }, [jobLocations, sendSubscribe]);

  useEffect(() => {
    if (!enabled) {
      clearPingInterval();
      clearReconnectTimer();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(NORMAL_CLOSE_CODE, 'Disabled');
        wsRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    connect();

    return () => {
      clearPingInterval();
      clearReconnectTimer();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(NORMAL_CLOSE_CODE, 'Unmount');
        wsRef.current = null;
      }
      setIsConnected(false);
    };
  }, [enabled, userId, connect, clearPingInterval, clearReconnectTimer]);

  return { isConnected, reconnectAttempts };
}
